import React, { useState, useEffect } from 'react';
import { Bell, ChevronLeft, ChevronRight, Info, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import API_BASE_URL from '../config/api';

const NoticeBoard = () => {
    const [notices, setNotices] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchNotices = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}/user/notices.php`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success) {
                    setNotices(data.data);
                }
            } catch (err) {
                console.error('Notice fetch failed');
            } finally {
                setLoading(false);
            }
        };
        fetchNotices();
    }, []);

    useEffect(() => {
        if (notices.length > 1) {
            const timer = setInterval(() => {
                setCurrentIdx((prev) => (prev + 1) % notices.length);
            }, 6000); // Change notice every 6 seconds
            return () => clearInterval(timer);
        }
    }, [notices]);

    if (loading || notices.length === 0) return null;

    const current = notices[currentIdx];

    const getTypeStyles = (type) => {
        switch (type) {
            case 'danger': return 'bg-red-50 text-red-700 border-red-100';
            case 'warning': return 'bg-amber-50 text-amber-700 border-amber-100';
            case 'success': return 'bg-green-50 text-green-700 border-green-100';
            default: return 'bg-blue-50 text-blue-700 border-blue-100';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'danger': return <AlertCircle size={20} className="text-red-500" />;
            case 'warning': return <AlertTriangle size={20} className="text-amber-500" />;
            case 'success': return <CheckCircle size={20} className="text-green-500" />;
            default: return <Info size={20} className="text-blue-500" />;
        }
    };

    return (
        <div className="relative overflow-hidden bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-100/50 group">
            {/* Header / Notice Title Bar */}
            <div className={`px-6 py-3 flex items-center justify-between border-b ${getTypeStyles(current.type)}`}>
                <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-white rounded-lg shadow-sm">
                        <Bell size={18} className="animate-bounce" />
                    </div>
                    <div>
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60 block">Official Notice</span>
                        <h4 className="text-sm font-bold leading-none">{current.title}</h4>
                    </div>
                </div>
                <div className="flex gap-1.5">
                    <button
                        onClick={() => setCurrentIdx((prev) => (prev - 1 + notices.length) % notices.length)}
                        className="p-1.5 hover:bg-white/50 rounded-lg transition-colors"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        onClick={() => setCurrentIdx((prev) => (prev + 1) % notices.length)}
                        className="p-1.5 hover:bg-white/50 rounded-lg transition-colors"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="p-6 flex gap-4 items-start">
                <div className="shrink-0 mt-1">
                    {getTypeIcon(current.type)}
                </div>
                <div className="flex-1">
                    <p className="text-gray-600 text-sm leading-relaxed animate-in fade-in slide-in-from-right-4 duration-500 font-medium">
                        {current.content}
                    </p>
                </div>
            </div>

            {/* Progress Bar (Autoplay Visualizer) */}
            <div className="absolute bottom-0 left-0 h-1 bg-gray-100 w-full overflow-hidden">
                <div
                    key={currentIdx}
                    className="h-full bg-indigo-600/30 animate-progress"
                    style={{ animationDuration: '6000ms' }}
                />
            </div>

            {/* Notice Indicators */}
            {notices.length > 1 && (
                <div className="absolute right-6 bottom-4 flex gap-1">
                    {notices.map((_, idx) => (
                        <div
                            key={idx}
                            className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentIdx ? 'w-4 bg-indigo-600' : 'w-1.5 bg-gray-200'
                                }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default NoticeBoard;
